import {Component, DoCheck, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Optional, Output, Self, SimpleChanges} from '@angular/core';
import moment, {Moment} from 'moment-timezone';
import {PlacementArray} from '@ng-bootstrap/ng-bootstrap/util/positioning';
import {AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NgControl, ValidationErrors, Validator} from '@angular/forms';
import {NgbDateAdapter, NgbDatepickerNavigateEvent, NgbDateStruct, NgbInputDatepicker, NgbTimeAdapter, NgbTimeStruct} from '@ng-bootstrap/ng-bootstrap';
import {Subject, timer} from 'rxjs';
import {AceLogger} from '@logging/ace-logger';
import {ElementIdGenerationService, ElementIdGenerator} from '@shared/services';
import {AceLoggerService} from '@logging/ace-logger.service';
import {MomentNgbDateAdapter} from '@shared/util/ng-bootstrap/moment-ngb-date-adapter';
import {filter, switchMap, takeUntil, tap} from 'rxjs/operators';
import {MomentNgbTimeAdapter} from '@shared/util/ng-bootstrap/moment-ngb-time-adapter';
import {objectEqualMomentAware} from '@shared/util/comparitors/objects-comparator';
import {I18nMessage} from '@shared/api/model/i18n';
import {DateTimeRangeService} from '@shared/components/date-time-range-picker/date-time-range.service';
import {ValidatorUtils} from '@shared/util/validators/validator-utils';

enum ControlNames {
  DATE_CONTROL_NAME = 'date',
  TIME_CONTROL_NAME = 'time',
  MILLIS_CONTROL_NAME = 'millis',
}

interface FormValue {
  [ControlNames.DATE_CONTROL_NAME]: Moment;
  [ControlNames.TIME_CONTROL_NAME]: Moment;
  // This value is never shown to the user (i.e. is never displayed in the template). It is stored here (alongside the
  // date and time values) so that the values produced by this component preserve the millisecond value of the value
  // that was set on this component (via {@link writeValue}). Of course, this control can be changed in the future to
  // display the millisecond value and allow the user to edit it
  [ControlNames.MILLIS_CONTROL_NAME]: number;
}

export enum DateTimeSelectComponentErrorKey {
  INVALID_FORMAT = 'invalidFormat',
  BEFORE_MIN_DATE = 'beforeMinDate',
  AFTER_MAX_DATE = 'afterMaxDate',
  INVALID_TIME = 'invalidTime'
}


@Component({
  selector: 'app-date-time-select',
  templateUrl: './date-time-select.component.html',
  styleUrls: ['./date-time-select.component.scss'],
  providers: [{
    provide: NgbDateAdapter,
    useExisting: forwardRef(() => DateTimeSelectComponent)
  }, {
    provide: NgbTimeAdapter,
    useClass: MomentNgbTimeAdapter
  }],
  standalone: false
})
/**
 * If this component is given a value (Moment) it will preserve the parts of the value that are not displayed, even when the
 * values that are displayed are changed. For example, if this component is not showing time, i.e. it is showing only the date,
 * it will preserve the time (including milliseconds) of the original value. e.g. If given a value of 2021-07-14 18:43:28.381
 * and is only showing the date, which is changed to 2022-09-08 by the user, the produced value will be 2022-09-08 18:43:28.381.
 * As the control never shows milliseconds, it will always use the milliseconds value from the value that was set on
 * this component.
 *
 * Also, note, this component is timezone aware. Hence, if the {@link timezone} @Input() is changed, this component will adjust its
 * current value to the new timezone.
 */
export class DateTimeSelectComponent implements OnChanges, OnInit, DoCheck, OnDestroy, ControlValueAccessor, Validator, NgbDateAdapter<Moment> {

  @Input() label: string;
  @Input() placeholder: string;
  @Input() readOnly = false;
  @Input() helpText: string;
  @Input() minDate: Moment;
  @Input() maxDate: Moment;

  @Input() showTime = false;
  @Input() showTimeSpinners = true;
  @Input() placement: PlacementArray = 'auto';
  @Input() timezone;
  @Input() translatedErrorMessagesByType: { [type: string]: I18nMessage } = {};
  @Input() isSmall = false;

  /**
   * Determines whether this component will display its own errors.
   *
   * The errors are shown under the most relevant input field, so it's nice :)
   */
  @Input() showErrors = true;

  /**
   *
   */
  @Input() showErrorsWhenPristine = false;

  @Output() navigate = new EventEmitter<NgbDatepickerNavigateEvent>();
  @Output() closed = new EventEmitter<void>();
  @Output() opened = new EventEmitter<void>();

  readonly ControlNames = ControlNames;
  readonly controls: UntypedFormGroup;

  // Still need to use NgbDateStruct for min/max date as moment date adaptor only works with form value
  minDateNgbDateStruct: NgbDateStruct;
  maxDateNgbDateStruct: NgbDateStruct;

  private effectiveTimezone = moment.tz.guess();
  /**
   * Because the {@link INVALID_FORMAT_ERROR_KEY} error is delayed by a timer, so that the user does not get an "invalid input" error
   * message as they are typing into the date input field, but only once they have paused typing (if the input is then invalid), rebuilding
   * the {@link errors} when the timer fires causes ExpressionChangedAfterItHasBeenCheckedError on the {@link errors} object.
   * Therefore, the {@link errorsNeedRebuild} is used to mark the {@link errors} for rebuild, and the {@link errors} object is rebuilt
   * on the next change detection run, i.e. using the {@link ngDoCheck} method.
   */
  private errorsNeedRebuild = false;
  errors: ValidationErrors;

  private readonly DEFAULT_TIME_STRUCT: NgbTimeStruct = {hour: 0, minute: 0, second: 0};
  private readonly DEFAULT_TIME: Moment = moment.utc(this.DEFAULT_TIME_STRUCT);
  private readonly DEFAULT_MILLIS = 0;
  private readonly DATE_FORMAT = 'YYYY-MM-DD';
  private readonly DATE_TIME_FORMAT = this.DATE_FORMAT + ' HH:mm:ss';

  /**
   * If the {@link minDate} or {@link maxDate} of this component change due to the angular bug
   * https://github.com/angular/angular/issues/25749 the {@link controls.valueChanges} observable emits a value, even if the value has not
   * changed. When this happens before this control was ever touched by a user, it causes ng-pristine property of this
   * control to change, which leads to ExpressionChangedAfterItHasBeenCheckedError.
   *
   * Also, distinctUntilChanged cannot be used on the {@link controls.valueChanges} observable, because the first time the observable
   * emits a value, distinctUntilChanged let's it through. In our case, because the call to onValidatorChange() could happen before this
   * control got touched by a user, the first value that distinctUntilChanged sees is what causes the
   * ExpressionChangedAfterItHasBeenCheckedError.
   *
   * Therefore, we are forced to "manually" keep track of the old value of the control in the {@link writeValue} function and in the
   * subscription to the {@link controls.valueChanges} observable.
   */
  private currentValue: FormValue = null;

  /**
   * When the user is typing in a date picker input field, do not want to show the error as they are typing. So delay showing the error
   * until {@link ERROR_TIMER_MS} has passed.
   */
  private hideInvalidInputError = false;
  private readonly ERROR_TIMER_MS = 500;
  private readonly timer = new Subject<void>();
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly logger: AceLogger;
  private readonly elementIdGenerator: ElementIdGenerator;

  private ngbDateAdapter: NgbDateAdapter<Moment>;
  private onChangeFn: (value: Moment) => void = (_: Moment) => {};
  private onTouchedFn: () => void = () => {};
  private validatorChangeFn: () => void = () => {};

  constructor(@Optional() @Self() private controlDir: NgControl,
              private dateTimeRangeService: DateTimeRangeService,
              fb: UntypedFormBuilder,
              logService: AceLoggerService,
              elementIdGenerationService: ElementIdGenerationService) {
    if (controlDir) {
      controlDir.valueAccessor = this;
    }
    this.logger = logService.create(DateTimeSelectComponent.name);
    this.elementIdGenerator = elementIdGenerationService.buildIdGenerator();
    // If the FormControl that is bound to this component (i.e. controlDir.control) is initialised with a default value when constructed
    // the writeValue method is called before ngOnInit or ngOnChanges. Hence, we need to:
    // 1. Build the controls form group so that we can assign the value to it when writeValue method is invoked
    // 2. Construct ngbDateAdapter with the "default" timezone. If this component is given a different timezone value later, it will be
    // rebuilt in ngOnChanges.
    this.controls = fb.group({
      [ControlNames.DATE_CONTROL_NAME]: new UntypedFormControl(),
      [ControlNames.TIME_CONTROL_NAME]: new UntypedFormControl(this.DEFAULT_TIME.clone()),
      [ControlNames.MILLIS_CONTROL_NAME]: new UntypedFormControl(this.DEFAULT_MILLIS),
    });
    this.ngbDateAdapter = new MomentNgbDateAdapter(this.effectiveTimezone);
  }

  ngOnInit() {
    if (this.controlDir) {
      const control = this.controlDir.control;
      control.setValidators(control.validator ? [control.validator, this.validate.bind(this)] : this.validate.bind(this));
      this.registerOnValidatorChange(control.updateValueAndValidity.bind(control));
      control.updateValueAndValidity();
    }

    this.controls.valueChanges.pipe(filter((formValue: FormValue) => !objectEqualMomentAware(this.currentValue, formValue)), takeUntil(this.ngUnsubscribe))
      .subscribe((value: FormValue) => {
        this.currentValue = value;
        if (this.onChangeFn) {
          this.onChangeFn(this.transformFormValueToMoment(value));
        }
      });

    this.timer.asObservable().pipe(tap(() => this.hideInvalidInputError = true,),
      switchMap(() => timer(this.ERROR_TIMER_MS)), takeUntil(this.ngUnsubscribe)).subscribe(_ => {
      this.hideInvalidInputError = false;
      this.markErrorsForRebuild();
    });

    this.controlDir.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.markErrorsForRebuild());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.timezone) {
      // Retrieve the value while the old timezone value is used
      const value = this.transformFormValueToMoment(this.currentValue);
      this.effectiveTimezone = this.timezone ? this.timezone : moment.tz.guess();
      // Update the adapter so that the new timezone value takes effect
      this.ngbDateAdapter = new MomentNgbDateAdapter(this.effectiveTimezone);
      // Update the value so that it gets updated to the new timezone
      this.updateValue(value);
    }

    let validatorUpdate = false;
    if (changes.minDate || changes.timezone) {
      this.minDateNgbDateStruct = this.ngbDateAdapter.fromModel(this.minDate);
      validatorUpdate = true;
    }

    if (changes.maxDate || changes.timezone) {
      this.maxDateNgbDateStruct = this.ngbDateAdapter.fromModel(this.maxDate);
      validatorUpdate = true;
    }

    if (validatorUpdate && this.validatorChangeFn) {
      this.validatorChangeFn();
    }
  }

  ngDoCheck(): void {
    if (this.shouldRebuildErrors()) {
      this.errors = this.buildErrors();
      this.clearErrorsForRebuild();
    }
  }

  formatTimestamp(ts: Moment): string {
    return this.dateTimeRangeService.formatTimestamp(
      ts,
      this.effectiveTimezone,
      this.showTime,
      this.DATE_FORMAT,
      this.DATE_TIME_FORMAT
    );
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  writeValue(value: Moment): void {
    this.logger.debug('writeValue', value);
    this.updateValue(value);
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.controls.disable({emitEvent: false});
    } else {
      this.controls.enable({emitEvent: false});
    }
  }

  validate(c: AbstractControl): ValidationErrors | null {
    const errors = this.validateDateAndTime(this.controls.value, this.minDate, this.maxDate);
    this.markErrorsForRebuild();
    if (errors && Object.keys(errors).length) {
      return errors;
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChangeFn = fn;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  fromModel(value: moment.Moment): NgbDateStruct {
    return this.ngbDateAdapter.fromModel(value);
  }

  toModel(date: NgbDateStruct): moment.Moment {
    return this.ngbDateAdapter.toModel(date);
  }

  onTouched(): void {
    if (this.onTouchedFn) {
      this.onTouchedFn();
    }
  }

  onDatePickerKeypress(): void {
    this.timer.next();
  }

  onNavigate(event: NgbDatepickerNavigateEvent) {
    this.navigate.next(event);
  }

  onClosed() {
    this.closed.next();
  }

  get dateFormat(): string {
    return this.DATE_FORMAT.toLocaleLowerCase();
  }

  /**
   * Needed to ensure that if multiple instances of this component exist on the page, the ids do not clash
   */
  getElementId(prefix: string): string {
    return this.elementIdGenerator.getId(prefix);
  }

  /**
   * Toggles the date picker window open or closed.
   *
   * When opening the date picker, emits an `opened` event. This is handled here
   * because `NgbInputDatePicker` provides a `closed` event but no `opened` event.
   *
   * A `closed` event is not emitted here because this handles the toggle button click but
   * the date picker can also be closed by clicking outside the picker window. */
  toggleDatePicker(datePicker: NgbInputDatepicker) {
    const isCurrentlyOpen = datePicker.isOpen();
    datePicker.toggle();
    if (!isCurrentlyOpen) {
      this.opened.emit();
    }
  }

  private updateValue(value: Moment): void {
    const vTz = value ? value.clone().tz(this.effectiveTimezone) : null;
    this.currentValue = {
      [ControlNames.DATE_CONTROL_NAME]: vTz ? vTz.clone().set(this.DEFAULT_TIME_STRUCT) : null,
      [ControlNames.TIME_CONTROL_NAME]: vTz ? moment.utc({hour: vTz.hour(), minute: vTz.minute(), second: vTz.second()}) : this.DEFAULT_TIME.clone(),
      [ControlNames.MILLIS_CONTROL_NAME]: vTz ? vTz.milliseconds() : this.DEFAULT_MILLIS
    };
    this.controls.setValue(this.currentValue, {emitEvent: false});
  }

  private transformFormValueToMoment(formValue: FormValue): Moment {
    if (formValue) {
      return this.combineDateAndTime(formValue[ControlNames.DATE_CONTROL_NAME], formValue[ControlNames.TIME_CONTROL_NAME], formValue[ControlNames.MILLIS_CONTROL_NAME]);
    } else {
      return null;
    }
  }

  private combineDateAndTime(date: Moment, time: Moment, millis: number): Moment {
    return moment.isMoment(date) && moment.isMoment(time) && date.isValid() && time.isValid() ? date.clone().set({
      hour: time.hour(),
      minute: time.minute(),
      second: time.second(),
      millisecond: millis
    }) : null;
  }

  private markErrorsForRebuild(): void {
    this.errorsNeedRebuild = true;
  }

  private clearErrorsForRebuild(): void {
    this.errorsNeedRebuild = false;
  }

  private shouldRebuildErrors(): boolean {
    return this.errorsNeedRebuild;
  }

  private buildErrors(): ValidationErrors {
    const displayErrors = ValidatorUtils.displayErrors(this.controlDir?.control);
    if (this.showErrorsWhenPristine || displayErrors) {
      const errors = this.controlDir?.control.errors;
      if (errors) {
        const controlErrors = Object.assign({}, errors);
        if (controlErrors[DateTimeSelectComponentErrorKey.INVALID_FORMAT] && this.hideInvalidInputError) {
          delete controlErrors[DateTimeSelectComponentErrorKey.INVALID_FORMAT];
        }
        // If there is a required error:
        // 1. But the value in the date input field is not null, do not show the required error, as this means
        // that there is invalid input instead
        // 2. Even if showErrorsWhenPristine is true, we do not want to show the required error if the control has not been touched
        if (controlErrors.required && (this.controls.get(ControlNames.DATE_CONTROL_NAME).value != null || !displayErrors)) {
          delete controlErrors.required;
        }

        return ValidatorUtils.normaliseErrors(controlErrors);
      }
    }

    return null;
  }

  /**
   * Return errors if there is a problem or a timestamp representing combines date and time if the input is valid.
   */
  private validateDateAndTime(formValue: FormValue, minDate: Moment, maxDate: Moment): ValidationErrors {
    const date: Moment = formValue[ControlNames.DATE_CONTROL_NAME];
    const time: Moment = formValue[ControlNames.TIME_CONTROL_NAME];

    if (date) {
      if (!moment.isMoment(date) || !date.isValid()) {
        return this.getValidationErrorsByKey(
            DateTimeSelectComponentErrorKey.INVALID_FORMAT,
            { messageKey: 'Invalid format', parameters: { format: this.dateFormat } });
      } else {
        const timeErrors = this.validateTime(time);
        if (timeErrors) {
          return timeErrors;
        } else {
          const ts = this.transformFormValueToMoment(formValue);
          if (minDate && ts.isBefore(minDate)) {
            return this.getValidationErrorsByKey(
                DateTimeSelectComponentErrorKey.BEFORE_MIN_DATE,
                { messageKey: 'Choose date later than', parameters: { date: this.formatTimestamp(minDate) } });
          } else if (maxDate && ts.isAfter(maxDate)) {
            return this.getValidationErrorsByKey(
                DateTimeSelectComponentErrorKey.AFTER_MAX_DATE,
                { messageKey: 'Choose date earlier than', parameters: {date: this.formatTimestamp(maxDate)} });
          } else {
            return null;
          }
        }
      }
    } else {
      return this.validateTime(time);
    }
  }

  private validateTime(time: Moment): ValidationErrors {
    if (this.showTime && (!moment.isMoment(time) || !time.isValid())) {
      return this.getValidationErrorsByKey(DateTimeSelectComponentErrorKey.INVALID_TIME, {messageKey: 'Invalid time'});
    } else {
      return null;
    }
  }

  private getValidationErrorsByKey(errorKey: DateTimeSelectComponentErrorKey, defaultMessage: I18nMessage): ValidationErrors {
    return {
      [errorKey]: this.translatedErrorMessagesByType[errorKey] || defaultMessage
    };
  }
}
